import type { Transaction } from '@/lib/transactions/types';
import { countedAmount, isFlowNeutral } from '@/lib/transactions/refunds';
import type { ScreenTransaction } from '../screen/types';

export interface MerchantRow<T extends Transaction = ScreenTransaction> {
  key: string;
  name: string;
  count: number;
  total: number;
  lastDate: string;
  /** Most-recent transaction for this merchant — used to render a sample row. */
  sampleTx: T;
}

/**
 * Aggregate the transactions into merchant rows for a single parent category.
 * Direction is intentionally ignored so details work for income, expenses,
 * refunds, and mixed slices; cash-flow-neutral rows are excluded.
 */
export function buildMerchantRows<T extends Transaction>(
  txs: readonly T[],
  parentCategoryId: string,
): MerchantRow<T>[] {
  const byKey = new Map<string, MerchantRow<T>>();
  for (const t of txs) {
    if (isFlowNeutral(t)) continue;
    const parent = t.parentCategoryId ?? t.categoryId;
    if (parent !== parentCategoryId) continue;
    // Counted remainder, not the face value — partial refunds shrink the total.
    const amount = Math.abs(countedAmount(t));
    if (amount === 0) continue;
    const name = (t.merchant ?? 'Sem comerciante').trim() || 'Sem comerciante';
    const key = name.toLocaleLowerCase('pt-BR');
    const cur = byKey.get(key);
    if (cur) {
      cur.count += 1;
      cur.total += amount;
      if (t.date > cur.lastDate) {
        cur.lastDate = t.date;
        cur.sampleTx = t;
      }
    } else {
      byKey.set(key, {
        key,
        name,
        count: 1,
        total: amount,
        lastDate: t.date,
        sampleTx: t,
      });
    }
  }
  return Array.from(byKey.values()).sort((a, b) => b.count - a.count || b.total - a.total);
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join('') || '?';
}
